import {isLeft} from 'fp-ts/lib/Either'
import reporter from 'io-ts-reporters'
import {set} from 'object-path-immutable'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {ENVIRONMENT_NAME} from '../lib/env'
import {Updator} from '../views/card-builder/editor'
import {
  InArgsV1,
  Payload,
  defaultInArgs,
  migratePayload
} from './journey-builder-config'
import {PostMongerConnection} from './postmonger'

/*
Example activity for reference
{
  "name": "",
  "id": "a44601b5-03d6-41d9-a729-11e4b4e5cdf8",
  "key": "REST-2",
  "type": "REST",
  "arguments": {
    "execute": {
      "inArguments": [],
      "outArguments": [],
      "url": "",
      "verb": "POST",
      "body": "",
      "header": "",
      "format": "json",
      "useJwt": true,
      "timeout": 2000
    }
  },
  "configurationArguments": {
    "save": {
      "url": "https://webhook.site/a34d4ccc-70be-4d42-a0a8-7ba5d5315ccf/save",
      "verb": "POST",
      "useJwt": false
    },
    "publish": {
      "url": "https://webhook.site/a34d4ccc-70be-4d42-a0a8-7ba5d5315ccf/publish",
      "verb": "POST",
      "useJwt": false
    },
    "stop": {
      "url": "https://webhook.site/a34d4ccc-70be-4d42-a0a8-7ba5d5315ccf/stop",
      "verb": "POST",
      "useJwt": false
    },
    "validate": {
      "url": "https://webhook.site/a34d4ccc-70be-4d42-a0a8-7ba5d5315ccf/validate",
      "verb": "POST",
      "useJwt": false
    },
    "applicationExtensionKey": "075d55af-f314-4d68-aac8-f0d7f86d3fee"
  },
  "metaData": {
    "isConfigured": false
  },
  "schema": {
    "arguments": {
      "execute": {
        "inArguments": [],
        "outArguments": []
      }
    }
  },
  "editable": true,
  "outcomes": [
    {
      "key": "b5f5da63-71be-4180-bfd8-bd02f78c6fa5",
      "next": "WAITBYDURATION-1",
      "arguments": {},
      "metaData": {
        "invalid": false
      }
    }
  ],
  "errors": null
}
*/

/** Data shape derived from state (represented how we receive it from SFMC) */
export type ActivityState = {
  data: {
    state?: InArgsV1
    metaData: {
      organisationId?: string
      environmentId?: string
      actionFlowId?: string
    }
    url?: string
  }
  setTargetUser: (v: string) => void
  setVariables: (
    v: InArgsV1['variables'] | Updator<InArgsV1['variables']>
  ) => void
  setMetadata: (
    v:
      | ActivityState['data']['metaData']
      | Updator<ActivityState['data']['metaData']>
  ) => void
  setUrl: (u: string) => void
  setFormValid: (valid: boolean) => void
  submissionAttempted: boolean
  isInitialised: boolean
}

const initialState = {
  payload: {},
  submissionAttempted: false,
  formInvalid: false,
  isInitialised: false
}

export const useJourneyBuilderConnection = (
  connection: PostMongerConnection
): ActivityState & {error?: string} => {
  const [state, setState] = useState<{
    payload: Payload | undefined
    submissionAttempted: boolean
    formInvalid: boolean
    isInitialised: boolean
  }>(initialState)
  const [error, setError] = useState<string>()

  // ref needed because we don't want to have to reset callbacks every time the
  // data changes, and we need the data to send with the updateActivity event
  const payloadRef = useRef(state)
  payloadRef.current = state

  // initial setup
  useEffect(() => {
    connection.on('initActivity', (data: any) => {
      const payload = migratePayload(data)
      if (isLeft(payload)) {
        console.error(reporter.report(payload))
        setError('Invalid activity configuration')
      } else {
        setState({
          // shouldn't have any state but clear out just in case
          ...initialState,
          payload: payload.right,
          isInitialised: true // show we've initialised
        })
      }
    })

    // when next is clicked in the ui, update - this closes the modal
    connection.on('clickedNext', () => {
      setState(st => ({...st, submissionAttempted: true}))
      if (!payloadRef.current.formInvalid) {
        console.warn('Submission blocked with invalid form state')
        connection.trigger('ready')
        return
      }

      let updated = payloadRef.current.payload
      const updates: [string, unknown][] = [
        ['metaData.isConfigured', true],
        ['arguments.execute.useJwt', true],
        ['arguments.execute.verb', 'POST'],
        ['arguments.execute.format', 'json']
      ]
      for (const update of updates) {
        updated = set(updated, ...update)
      }

      updated = removeEmptyVariables(updated as Payload)

      console.info('Submitting valid form state')
      connection.trigger('updateActivity', updated)
    })

    // for debugging, trigger by using e.g. connection.trigger('clickedNext')
    if (ENVIRONMENT_NAME !== 'master') {
      // @ts-ignore
      window.connection = connection // eslint-disable-line functional/immutable-data
    }

    // now that listeners are registered, we're ready
    connection.trigger('ready')
  }, [connection])

  const data = useMemo(
    (): ActivityState['data'] => ({
      state:
        state.payload?.arguments?.execute?.inArguments?.[0] ?? defaultInArgs,
      metaData: {
        organisationId: state.payload?.metaData?.atomic_io?.organisationId,
        environmentId: state.payload?.metaData?.atomic_io?.environmentId,
        actionFlowId: state.payload?.metaData?.atomic_io?.actionFlowId
      },
      url: state.payload?.arguments?.execute?.url
    }),
    [state.payload]
  )

  /** This actually sets inArgs[0] but we're only using one argument currently */
  const setInArgs: (
    v:
      | Omit<InArgsV1, 'schemaVersion'>
      | Updator<Omit<InArgsV1, 'schemaVersion'> | undefined>
  ) => void = useCallback(value => {
    setState(st => {
      const existingArgs =
        st.payload?.arguments?.execute?.inArguments?.[0] ?? defaultInArgs

      // perform the update on the transformed state value
      const mut_updatedState =
        typeof value === 'function' ? value(existingArgs) : value

      return set(st, 'payload.arguments.execute.inArguments.0', {
        ...mut_updatedState,
        schemaVersion: '1'
      })
    })
  }, [])

  const setVariables = useCallback(
    (variables: InArgsV1['variables'] | Updator<InArgsV1['variables']>) => {
      setInArgs(st => ({
        ...st,
        variables: (typeof variables === 'function'
          ? variables(st?.variables)
          : variables) as any
      }))
    },
    [setInArgs]
  )

  const setTargetUser = useCallback(
    (targetUser: string) => {
      setInArgs(st => ({
        ...st,
        targetUserIds: [targetUser]
      }))
    },
    [setInArgs]
  )

  const setMetadata: (
    v:
      | ActivityState['data']['metaData']
      | Updator<ActivityState['data']['metaData']>
  ) => void = useCallback(value => {
    setState(st => {
      const existingMetadata = st.payload?.metaData?.atomic_io ?? {}

      // perform the update on the transformed state value
      const mut_updatedState =
        typeof value === 'function' ? value(existingMetadata) : value

      return set(st, 'payload.metaData.atomic_io', mut_updatedState)
    })
  }, [])

  const setUrl = useCallback((url: string) => {
    setState(st => {
      return set(st, 'payload.arguments.execute.url', url)
    })
  }, [])

  const setFormValid = useCallback((valid: boolean) => {
    setState(st => ({...st, formInvalid: valid}))
  }, [])

  return {
    error,
    data,
    setMetadata,
    setTargetUser,
    setVariables,
    setUrl,
    setFormValid,
    submissionAttempted: state.submissionAttempted,
    isInitialised: state.isInitialised
  }
}

/** when creating placeholders we might have some empty args (e.g. {"": "foo"} - purge them before submitting */
const removeEmptyVariables = (payload: Payload): Payload => {
  const variables = payload.arguments?.execute?.inArguments?.[0]?.variables
  if (!variables) {
    return payload
  }

  const updated: Payload = {
    ...payload,
    arguments: {
      ...payload.arguments,
      execute: {
        ...payload.arguments?.execute,
        inArguments: [
          {
            ...(payload.arguments?.execute?.inArguments?.[0] ?? defaultInArgs),
            variables: Object.fromEntries(
              Object.entries(variables ?? {}).filter(([key]) => {
                return !!key
              })
            )
          }
        ]
      }
    }
  }
  return updated
}
